import {
	addMilliseconds,
	addMinutes,
	differenceInMilliseconds,
	eachMinuteOfInterval,
	endOfDay,
	format,
	isAfter,
	isBefore,
	isSameDay,
	isValid,
	parse,
	roundToNearestMinutes,
	startOfDay,
} from "date-fns";

import type { CalendarErrorType, VideoConferencingErrorType } from "./types";
import { CALENDAR_ERROR_TYPE, VIDEO_CONFERENCING_ERROR_TYPE } from "./types";

import type { MeetingFormSchema } from "./meeting-form-schema";

// Type representing form values that may have undefined dates during editing
export type MeetingFormValuesWithOptionalDates = Omit<MeetingFormSchema, "startDate" | "endDate"> & {
	startDate: Date | undefined;
	endDate: Date | undefined;
};

// Default meeting duration in milliseconds (1 hour)
const DEFAULT_MEETING_DURATION_MS = 60 * 60 * 1000;

export const getDefaultMeetingTimes = (date: Date = new Date()): { startDateTime: Date; endDateTime: Date } => {
	const now = new Date();
	const baseDate = date < now ? now : date;

	let startDateTime = roundToNearestMinutes(baseDate, { nearestTo: 15 });

	if (startDateTime <= now) {
		startDateTime = addMinutes(startDateTime, 15);
	}

	const endDateTime = addMinutes(startDateTime, 60);

	return { startDateTime, endDateTime };
};

export const generateTimeOptions = (selectedDate: Date = new Date()): Array<{ label: string; value: string }> => {
	const start = startOfDay(selectedDate);
	const end = endOfDay(selectedDate);
	const times = eachMinuteOfInterval({ start, end }, { step: 15 });

	return times.map((time) => ({
		label: format(time, "h:mma").toLowerCase(),
		value: format(time, "HH:mm"),
	}));
};

const calculateNewEndDateTime = (
	originalStartDate: Date | undefined,
	originalStartTime: string,
	originalEndDate: Date | undefined,
	originalEndTime: string,
	newStartDate: Date,
	newStartTime: string
): { newEndDate: Date; newEndTime: string } => {
	let durationMs = DEFAULT_MEETING_DURATION_MS;

	// Only calculate duration from original dates if both are valid
	if (originalStartDate && originalEndDate && isValid(originalStartDate) && isValid(originalEndDate)) {
		const originalStartDateTime = parse(originalStartTime, "HH:mm", originalStartDate);
		const originalEndDateTime = parse(originalEndTime, "HH:mm", originalEndDate);
		const calculatedDuration = differenceInMilliseconds(originalEndDateTime, originalStartDateTime);

		if (calculatedDuration > 0) {
			durationMs = calculatedDuration;
		}
	}

	const newStartDateTime = parse(newStartTime, "HH:mm", newStartDate);
	const newEndDateTime = addMilliseconds(newStartDateTime, durationMs);

	return {
		newEndDate: startOfDay(newEndDateTime),
		newEndTime: format(newEndDateTime, "HH:mm"),
	};
};

export const handleStartDateChange = (
	val: Date | null | undefined,
	formValues: MeetingFormValuesWithOptionalDates,
	onFormChange: (newValues: Partial<MeetingFormSchema>) => void
): void => {
	if (!val) {
		onFormChange({ startDate: undefined });
		return;
	}

	const newStartDate = startOfDay(val);
	let changes: Partial<MeetingFormSchema> = { startDate: newStartDate };

	// Only adjust end date/time if end date is defined and valid
	if (formValues.endDate && isValid(formValues.endDate)) {
		const needsEndDateAdjustment =
			isAfter(newStartDate, formValues.endDate) ||
			(isSameDay(newStartDate, formValues.endDate) && formValues.startTime >= formValues.endTime);

		if (needsEndDateAdjustment) {
			const { newEndDate, newEndTime } = calculateNewEndDateTime(
				formValues.startDate,
				formValues.startTime,
				formValues.endDate,
				formValues.endTime,
				newStartDate,
				formValues.startTime
			);
			changes = { ...changes, endDate: newEndDate, endTime: newEndTime };
		}
	}

	onFormChange(changes);
};

export const handleEndDateChange = (
	val: Date | null | undefined,
	formValues: MeetingFormValuesWithOptionalDates,
	onFormChange: (newValues: Partial<MeetingFormSchema>) => void
): void => {
	if (!val) {
		onFormChange({ endDate: undefined });
		return;
	}

	const newEndDate = startOfDay(val);
	let changes: Partial<MeetingFormSchema> = { endDate: newEndDate };

	// Only adjust start date/time if start date is defined and valid
	if (formValues.startDate && isValid(formValues.startDate)) {
		if (isBefore(newEndDate, formValues.startDate)) {
			changes = { ...changes, startDate: newEndDate };
			if (formValues.endTime <= formValues.startTime) {
				const { newEndDate: adjustedEndDate, newEndTime } = calculateNewEndDateTime(
					newEndDate,
					formValues.startTime,
					newEndDate,
					formValues.endTime,
					newEndDate,
					formValues.startTime
				);
				if (!isSameDay(adjustedEndDate, newEndDate)) {
					changes = { ...changes, endDate: adjustedEndDate, endTime: newEndTime };
				} else {
					changes = { ...changes, endTime: newEndTime };
				}
			}
		} else if (isSameDay(formValues.startDate, newEndDate) && formValues.endTime <= formValues.startTime) {
			const { newEndDate: adjustedEndDate, newEndTime } = calculateNewEndDateTime(
				formValues.startDate,
				formValues.startTime,
				newEndDate,
				formValues.endTime,
				formValues.startDate,
				formValues.startTime
			);
			if (!isSameDay(adjustedEndDate, newEndDate)) {
				changes = { ...changes, endDate: adjustedEndDate, endTime: newEndTime };
			} else {
				changes = { ...changes, endTime: newEndTime };
			}
		}
	}

	onFormChange(changes);
};

export const handleStartTimeChange = (
	option: { value: string },
	formValues: MeetingFormValuesWithOptionalDates,
	onFormChange: (newValues: Partial<MeetingFormSchema>) => void
): void => {
	const newStartTime = option.value;
	let changes: Partial<MeetingFormSchema> = { startTime: newStartTime };

	// Only adjust end date/time if both dates are defined and valid
	if (
		formValues.startDate &&
		formValues.endDate &&
		isValid(formValues.startDate) &&
		isValid(formValues.endDate) &&
		isSameDay(formValues.startDate, formValues.endDate) &&
		newStartTime >= formValues.endTime
	) {
		const { newEndDate, newEndTime } = calculateNewEndDateTime(
			formValues.startDate,
			formValues.startTime,
			formValues.endDate,
			formValues.endTime,
			formValues.startDate,
			newStartTime
		);
		changes = { ...changes, endDate: newEndDate, endTime: newEndTime };
	}

	onFormChange(changes);
};

/**
 * Checks if the date/time combination is valid (end datetime is after start datetime).
 * Returns false if either date is undefined or if end is not after start.
 */
export const areDatesInValidOrder = (formValues: MeetingFormValuesWithOptionalDates): boolean => {
	if (!formValues.startDate || !formValues.endDate) {
		return false;
	}

	if (!isValid(formValues.startDate) || !isValid(formValues.endDate)) {
		return false;
	}

	const startDateTime = parse(formValues.startTime, "HH:mm", formValues.startDate);
	const endDateTime = parse(formValues.endTime, "HH:mm", formValues.endDate);

	return isAfter(endDateTime, startDateTime);
};

/**
 * Returns default form values for creating a new meeting.
 * Start time is rounded to the nearest 15-minute interval, with a 1-hour default duration.
 */
export const getDefaultFormValues = (): MeetingFormSchema => {
	const defaultTimes = getDefaultMeetingTimes();
	return {
		name: "",
		startDate: startOfDay(defaultTimes.startDateTime),
		endDate: startOfDay(defaultTimes.endDateTime),
		startTime: format(defaultTimes.startDateTime, "HH:mm"),
		endTime: format(defaultTimes.endDateTime, "HH:mm"),
		description: "",
		carryOver: false,
		addToCalendar: false,
		videoConferencingProvider: null,
		workspaceId: undefined,
	};
};

export const isCalendarErrorRequiringModal = (
	error: unknown
): { requiresModal: boolean; errorType?: CalendarErrorType } => {
	const err = error as { response?: { status?: number }; data?: { meta?: { calendarErrorType?: string } } };

	if (err?.response?.status !== 422) {
		return { requiresModal: false };
	}

	const calendarErrorType = err?.data?.meta?.calendarErrorType;

	if (calendarErrorType === "CREATION_FAILED") {
		return { requiresModal: true, errorType: CALENDAR_ERROR_TYPE.CREATE_FAILED };
	}

	if (calendarErrorType === "UPDATE_FAILED") {
		return { requiresModal: true, errorType: CALENDAR_ERROR_TYPE.UPDATE_FAILED };
	}

	return { requiresModal: false };
};

export const isVideoConferencingErrorRequiringModal = (
	error: unknown
): { requiresModal: boolean; errorType?: VideoConferencingErrorType } => {
	const err = error as {
		response?: { status?: number };
		data?: { meta?: { videoConferencingErrorType?: string } };
	};

	if (err?.response?.status !== 422) {
		return { requiresModal: false };
	}

	const videoConferencingErrorType = err?.data?.meta?.videoConferencingErrorType;

	if (videoConferencingErrorType === "CREATION_FAILED") {
		return { requiresModal: true, errorType: VIDEO_CONFERENCING_ERROR_TYPE.CREATE_FAILED };
	}
	if (videoConferencingErrorType === "UPDATE_FAILED") {
		return { requiresModal: true, errorType: VIDEO_CONFERENCING_ERROR_TYPE.UPDATE_FAILED };
	}
	if (videoConferencingErrorType === "DELETION_FAILED") {
		return { requiresModal: true, errorType: VIDEO_CONFERENCING_ERROR_TYPE.DELETE_FAILED };
	}

	return { requiresModal: false };
};
